"use client";

/**
 * เติมเงินทรูมันนี่ด้วยซองอั่งเปา — ลูกค้าสร้างซองแบบส่งให้คนเดียว แล้ววางลิงก์ตรงนี้
 *
 * ระบบกดรับซองเข้าวอลเล็ทร้านให้ทันที (ผ่าน mini-app-api) แล้ว poll สถานะจนแอดมิน
 * ยืนยัน — ใช้เส้น poll เดียวกับหน้าสลิป (/api/topup-truewallet) เพราะคำขอทุก
 * ช่องทางของยูสอยู่รวมกัน
 */

import React from "react";

import { Alert, Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import RedeemIcon from "@mui/icons-material/Redeem";

import fetcher from "@/libs/fetcher";

type TopupRequestRow = {
  id: string;
  status: "pending" | "verified" | "approved" | "rejected";
  amount: number | null;
};

type Phase =
  | { name: "idle" }
  | { name: "submitting" }
  | { name: "waiting"; requestId: string; amountBaht: string | null }
  | { name: "approved"; amountBaht: string | null }
  | { name: "rejected" }
  | { name: "error"; message: string };

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_TRIES = 60; // ~3 นาที

/** ลิงก์ซองต้องมี v= หรือเป็นรหัสยาว ๆ — เช็คคร่าว ๆ ฝั่ง client กันกดพลาด */
function looksLikeVoucher(input: string): boolean {
  const s = input.trim();
  return /[?&]v=[A-Za-z0-9]{20,}/.test(s) || /^[A-Za-z0-9]{20,}$/.test(s);
}

export default function TruewalletVoucher() {
  const [link, setLink] = React.useState("");
  const [phase, setPhase] = React.useState<Phase>({ name: "idle" });
  const pollTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, []);

  const pollStatus = React.useCallback(
    (requestId: string, amountBaht: string | null, tries: number) => {
      if (tries >= POLL_MAX_TRIES) {
        setPhase({
          name: "error",
          message:
            "ระบบกำลังตรวจสอบซองอยู่ เครดิตจะเข้าอัตโนมัติเมื่อตรวจเสร็จ ไม่ต้องส่งซ้ำ",
        });
        return;
      }
      pollTimer.current = setTimeout(async () => {
        try {
          const res = await fetcher<{ data: TopupRequestRow[] }>(
            "/api/topup-truewallet",
          );
          const row = res.data.find((r) => r.id === requestId);
          if (row?.status === "approved") {
            setPhase({ name: "approved", amountBaht });
            return;
          }
          if (row?.status === "rejected") {
            setPhase({ name: "rejected" });
            return;
          }
        } catch {
          // network ชั่วคราว — poll ต่อ
        }
        pollStatus(requestId, amountBaht, tries + 1);
      }, POLL_INTERVAL_MS);
    },
    [],
  );

  const busy = phase.name === "submitting" || phase.name === "waiting";

  const handleRedeem = async () => {
    const voucher = link.trim();
    if (!looksLikeVoucher(voucher)) {
      setPhase({
        name: "error",
        message: "ลิงก์ซองไม่ถูกต้อง — ก๊อปลิงก์ซองจากแอปทรูมันนี่มาวางให้ครบ",
      });
      return;
    }

    setPhase({ name: "submitting" });
    try {
      const res = await fetcher<{
        status: string;
        data?: { request_id: string; amount_baht: string };
      }>("/api/topup-truewallet-voucher", {
        method: "post",
        body: { voucher },
      });
      const requestId = res.data?.request_id ?? "";
      const amountBaht = res.data?.amount_baht ?? null;
      setLink("");
      setPhase({ name: "waiting", requestId, amountBaht });
      pollStatus(requestId, amountBaht, 0);
    } catch (error) {
      const axiosError = error as {
        response?: {
          status?: number;
          data?: { status?: string; message?: string };
        };
      };
      const code = axiosError.response?.data?.status;
      if (code === "duplicate" || code === "used") {
        setPhase({
          name: "error",
          message: "ซองใบนี้ถูกใช้ไปแล้ว หรือหมดอายุ — ใช้ซ้ำไม่ได้ครับ",
        });
      } else if (code === "invalid") {
        setPhase({
          name: "error",
          message:
            axiosError.response?.data?.message ??
            "ซองใบนี้ใช้ไม่ได้ — ต้องเป็นซองแบบส่งให้คนเดียวที่ยังไม่มีใครรับ",
        });
      } else if (axiosError.response?.status === 401) {
        setPhase({ name: "error", message: "กรุณาเข้าสู่ระบบก่อนเติมเงินครับ" });
      } else {
        setPhase({
          name: "error",
          message:
            axiosError.response?.data?.message ??
            "รับซองไม่สำเร็จ ลองใหม่อีกครั้งครับ",
        });
      }
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
      <Typography variant="h5" fontWeight={700}>
        เติมด้วยซองอั่งเปา
      </Typography>

      {/* วิธีทำ */}
      <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(255,255,255,0.06)" }}>
        <Typography variant="body2" sx={{ opacity: 0.85, mb: 1 }}>
          1. เปิดแอปทรูมันนี่ → อั่งเปา → เลือกยอด
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.85, mb: 1 }}>
          2. เลือกแบบ <b>&ldquo;ส่งให้คนเดียว&rdquo;</b> (ห้ามแจกกลุ่ม/สุ่ม)
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.85 }}>
          3. ก๊อปลิงก์ซองมาวางด้านล่าง แล้วกด &ldquo;รับเครดิต&rdquo;
        </Typography>
      </Box>

      {/* วางลิงก์ + ปุ่ม */}
      <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(255,255,255,0.06)" }}>
        <TextField
          fullWidth
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="วางลิงก์ซอง https://gift.truemoney.com/campaign/?v=..."
          disabled={busy}
          size="small"
          sx={{ mb: 1.5 }}
        />
        <Button
          variant="contained"
          size="large"
          fullWidth
          disabled={busy || !link.trim()}
          onClick={handleRedeem}
          startIcon={
            busy ? <CircularProgress size={20} color="inherit" /> : <RedeemIcon />
          }
        >
          {phase.name === "submitting"
            ? "กำลังรับซอง..."
            : phase.name === "waiting"
              ? "กำลังเติมเครดิต..."
              : "รับเครดิต"}
        </Button>
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1, opacity: 0.6 }}
        >
          ⚠️ ต้องเป็นซองแบบส่งให้คนเดียว และยังไม่มีใครรับ · ซองหนึ่งใบใช้ได้ครั้งเดียว
        </Typography>
      </Box>

      {/* ผลลัพธ์ */}
      {phase.name === "waiting" && (
        <Alert severity="info" icon={<CircularProgress size={18} />}>
          รับซองแล้ว{phase.amountBaht ? ` ยอด ${phase.amountBaht} บาท` : ""} —
          กำลังเติมเครดิต รอสักครู่ครับ (ไม่ต้องปิดหน้านี้)
        </Alert>
      )}
      {phase.name === "approved" && (
        <Alert severity="success">
          🎉 เติมเครดิต{phase.amountBaht ? ` ${phase.amountBaht} บาท` : ""}เรียบร้อยแล้ว
          ขอบคุณครับ
        </Alert>
      )}
      {phase.name === "rejected" && (
        <Alert severity="error">
          รายการไม่ผ่านการตรวจสอบ กรุณาติดต่อแอดมินครับ
        </Alert>
      )}
      {phase.name === "error" && <Alert severity="warning">{phase.message}</Alert>}
    </Box>
  );
}
